// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ConfigHandler from '../loaders/handlers/ConfigHandler';
import ArrayUtils from '../util/ArrayUtils';
import ObjectUtils from '../util/ObjectUtils';
import Composer from '../renderer/pass/Composer';
import RenderPass from '../renderer/pass/RenderPass';
import FullscreenPass from '../renderer/pass/FullscreenPass';
import ShaderLib from '../renderer/shaders/ShaderLib';
import PassLib from '../passpack/PassLib';

/**
 * Handler for loading posteffects into engine
 */
class PosteffectsHandler extends ConfigHandler {
  constructor() {
    super(...arguments);
    this._composer = new Composer();
    this._renderList = this.world.getSystem('RenderSystem').renderList;
    this._renderPass = new RenderPass(this._renderList, null, true);
    this._outPass = new FullscreenPass(ObjectUtils.deepClone(ShaderLib.copy));
    this._outPass.renderToScreen = true;
  }

  /**
   * Removes the posteffects, i e removes the composer from rendersystem.
   * @param {string} ref
   */
  _remove(ref) {
    const renderSystem = this.world.getSystem('RenderSystem');
    ArrayUtils.remove(renderSystem.composers, this._composer);

    this._objects.delete(ref);

    if (this.world) {
      this._composer.destroy(this.world.sumerianRunner.renderer);
    }

    this._composer = new Composer();
  }

  /**
   * Creates an empty array which will hold the posteffects/RenderPasses
   * @returns {Entity}
   * @private
   */
  _create() {
    return [];
  }

  /**
   * Creates/updates/removes a posteffectconfig
   * @param {string} ref
   * @param {Object} config
   * @param {Object} options
   * @returns {Promise} Resolves with the updated posteffectsarray or null if removed
   */
  _update(ref, config, options) {
    const that = this;
    return ConfigHandler.prototype._update.call(this, ref, config, options).then((posteffects) => {
      if (!posteffects) { return; }

      const oldEffects = posteffects.slice();
      const promises = [];
      ObjectUtils.forEach(config.posteffects, (effectConfig) => {
        promises.push(that._updateEffect(effectConfig, oldEffects, options));
      }, null, 'sortValue');

      return Promise.all(promises).then((effects) => {
        let i;
        for (i = 0; i < effects.length; i++) {
          posteffects[i] = effects[i];
        }

        posteffects.length = i;
        return posteffects;
      });
    }).then((posteffects) => {
      if (!posteffects) { return; }

      const enabled = posteffects.some(effect => effect.enabled);
      const renderSystem = that.world.getSystem('RenderSystem');
      const composer = that._composer;

      // If there are any enabled, add them
      if (enabled) {
        composer.passes = [];
        composer.addPass(that._renderPass);
        for (let i = 0; i < posteffects.length; i++) {
          const posteffect = posteffects[i];
          if (posteffect && posteffect.enabled) {
            composer.addPass(posteffects[i]);
          }
        }
        composer.addPass(that._outPass);
        if (renderSystem.composers.indexOf(composer) === -1) {
          renderSystem.composers.push(composer);
        }
      } else {
        // No posteffects, remove composer
        ArrayUtils.remove(renderSystem.composers, that._composer);
      }

      return posteffects;
    });
  }

  /**
   * Finds the already created effect from the configs id or creates a new one and updates it
   * according to config
   * @param {Object} originalConfig
   * @param {Array<RenderPass>} posteffects array of engine posteffects/Renderpasses
   * @param {Object} options
   * @returns {RenderPass} effect
   */
  _updateEffect(originalConfig, posteffects, options) {
    // this gets mutated
    const config = ObjectUtils.deepClone(originalConfig);

    const that = this;
    function loadConfig(key, id) {
      return that._load(id, options).then((object) => {
        config.options[key] = object;
      });
    }

    // ArrayUtils.find
    let effect;
    for (let i = 0; i < posteffects.length; i++) {
      if (posteffects[i].id === config.id) {
        effect = posteffects[i];
        break;
      }
    }

    if (!effect) {
      if (!PassLib[config.type]) {
        return null;
      }
      effect = new PassLib[config.type](config.id, this._renderList);
    }

    const promises = [];
    for (let i = 0; i < PassLib[config.type].options.length; i++) {
      const option = PassLib[config.type].options[i];
      const key = option.key;
      const type = option.type;

      if (type === 'texture') {
        if (config.options[key] && config.options[key].textureRef && config.options[key].enabled) {
          promises.push(loadConfig(key, config.options[key].textureRef));
        } else {
          config.options[key] = null;
        }
      } else if (type === 'entity') {
        if (config.options[key] && config.options[key].entityRef && config.options[key].enabled) {
          promises.push(loadConfig(key, config.options[key].entityRef));
        } else {
          config.options[key] = null;
        }
      }
    }

    return Promise.all(promises).then(() => {
      effect.update(config);
      return effect;
    });
  }
}

ConfigHandler._registerClass('posteffects', PosteffectsHandler);

export default PosteffectsHandler;
